/**
 * Pricing — mirrors the REAL plan definitions in backend/access_control.py
 * (TIER_LIMITS). Do not add features here that the product doesn't have.
 */
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { marketingEvent } from '@/lib/marketing';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 19,
    blurb: 'For single-site teams getting off spreadsheets.',
    features: [
      'Up to 10 employees',
      'Weekly rota builder & publishing',
      'Kiosk clock in & out (PIN)',
      'Attendance & schedule check',
      'Employee portal & staff resources',
      'In-app & push shift notifications',
      'Employee roster import (CSV/XLSX)',
    ],
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49,
    blurb: 'For busy restaurants that want payroll prep done for them.',
    features: [
      'Up to 50 employees',
      'Everything in Starter',
      'Payroll runs & payslip drafts',
      'Schedule templates',
    ],
    highlighted: true,
  },
  {
    id: 'business',
    name: 'Business',
    price: 149,
    blurb: 'For larger operations that need room to grow.',
    features: [
      'Unlimited employees',
      'Everything in Pro',
      'GDPR admin tools',
      'Priority support',
    ],
    highlighted: false,
  },
];

export function PricingSection() {
  return (
    <section id="pricing" className="scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center">
          <Badge className="bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-soft)] rounded-full px-3 py-1" data-testid="marketing-pricing-badge">
            No per-employee pricing
          </Badge>
          <h2 className="mt-4 text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="marketing-pricing-heading">
            Simple pricing that doesn&apos;t punish growth
          </h2>
          <p className="mt-3 text-base text-[color:var(--brand-text-muted)]">
            One flat monthly price per plan. VAT included. Save 2 months with yearly billing.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5 items-stretch">
          {PLANS.map((plan) => (
            <article
              key={plan.id}
              className={`surface-card p-6 flex flex-col ${plan.highlighted ? 'ring-1 ring-[rgba(200,74,47,0.35)] shadow-[0_18px_50px_rgba(200,74,47,0.10)]' : ''}`}
              data-testid={`marketing-pricing-${plan.id}-card`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-semibold">{plan.name}</h3>
                {plan.highlighted && (
                  <span className="pill bg-[color:var(--brand-primary)] text-white !text-[10px]">Most popular</span>
                )}
              </div>
              <p className="mt-1 text-sm text-[color:var(--brand-text-muted)]">{plan.blurb}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-semibold" data-testid={`marketing-pricing-${plan.id}-price`}>£{plan.price}</span>
                <span className="text-sm text-[color:var(--brand-text-muted)]">/month</span>
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--brand-success)]" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                asChild
                className={`mt-6 h-11 rounded-xl w-full font-semibold transition-colors duration-150 ${
                  plan.highlighted
                    ? 'bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white'
                    : 'bg-white border border-[color:var(--brand-border)] text-[color:var(--brand-text)] hover:bg-[color:var(--brand-bg-2)]'
                }`}
                variant={plan.highlighted ? 'default' : 'outline'}
              >
                <Link
                  to="/signup"
                  onClick={() => marketingEvent('click_start_trial', { placement: `pricing_${plan.id}` })}
                  data-testid={`marketing-pricing-${plan.id}-start-trial-button`}
                >
                  Start Your Free Trial
                </Link>
              </Button>
            </article>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-[color:var(--brand-text-muted)]" data-testid="marketing-pricing-footnote">
          Every plan starts with a 14-day free trial. No setup fee. Cancel any time.
        </p>
      </div>
    </section>
  );
}
